<template>
  <PublicProfileLayout :role="role" :innerUsage="innerMap">
    <div class="public-profile-wrapper" v-if="profile">
      <div class="surface-section min-h-screen">
        <div>
          <div class="banner-responsive" :style="bannerStyleObject"></div>
          <div class="overlap-section col-12 md:col-10 flex justify-content-center align-items-center mx-auto">
            <div class="flex flex-column md:flex-row align-items-center justify-content-between w-full mt-3 gap-3">
              <div class="flex flex-column md:flex-row align-items-center gap-3 text-center md:text-left">
                <img :src="profileImage" class="profile-image" alt="Profile" />
                <div class="profile-info">
                  <div class="flex items-center gap-2 mt-4">
                    <h1 class="H5-SemiBold m-0 leading-none">{{ profile.username }}</h1>
                    
                    <div v-if="profile.verificationStatus === 'approved'" class="flex align-items-center gap-1">

                    <!-- Verified Badge -->
                    <svg 
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24" fill="currentColor" class="flex-shrink-0"
                      style="width: 26px; height: 26px; color: #1adc06;" v-tooltip.top="'Verified Tutor'">
                      <path fill-rule="evenodd" d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 
                      013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491
                      0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49
                      0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49
                      0 011.307-3.497 4.491 4.491 0 013.497-1.307zm7.007 6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75
                      0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clip-rule="evenodd" />
                    </svg>

                      <span
                        class="font-medium"
                        style="color: #1adc06;"
                      >
                        Verified
                      </span>
                    </div>

                  </div>
                  <p class="caption m-0 mt-2">{{ classTypeInfo }}</p>
                  <p class="caption m-0 mt-1">{{ formattedTutorType }} at ILearn'd</p>
                </div>
              </div>
              <div class="flex flex-wrap gap-3 teal-button" v-if="!innerMap">
                <Button class="teal-button-small w-full md:w-auto" label="Enroll" @click="requestClass()" v-if="canEnroll"/>
                <a :href="profile?.email ? `mailto:${profile.email}?subject=Contact%20Request&body=Hello` : 'mailto:support@ilearndsl.com?subject=Contact%20Request&body=Hello'"
                  target="_blank" rel="noopener">
                  <Button class="teal-button-transparent-small w-full md:w-auto" label="Contact me" />
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-column col-12 md:col-10 mt-4 justify-content-center align-items-center mx-auto">
          <div class="card w-full">
            <Tabs value="0" lazy>
              <TabList>
                <Tab class="Label" v-for="tab in tabs" :key="tab.title" :value="tab.value">{{ tab.title
                  }}</Tab>
              </TabList>
              <TabPanels>
                <TabPanel v-for="tab in tabs" :key="tab.value" :value="tab.value">
                  <component :is="tab.component" :calendarOptions="calendarOptions" :profileData="profile"
                    :feedbacks="feedbacks" :reviewsPagination="reviewsPagination" :notices="publishedNotices"
                    @change-review-page="onReviewPageChange" v-if="profile" />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
    <ConfirmDialog></ConfirmDialog>
  </PublicProfileLayout>
</template>

<script setup>
import { ref, computed, shallowRef, onMounted, defineProps, watchEffect } from "vue";
import { useRoute } from 'vue-router';
import router from "@/router";
import * as api from '@/api/tutor';
import * as studentApi from '@/api/student';
import { capitalize } from "@/utils/cleanups";
// import { notifyInfo } from "@/utils/notification";
import { useAuthStore } from '@/stores/authStore';
import { occupation } from '@/utils/options';

// PrimeVue and FullCalendar Imports
import Button from 'primevue/button';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import { useConfirm } from "primevue/useconfirm";

// Layout and Image Imports
import PublicProfileLayout from "@/layout/PublicProfileLayout.vue";
import bannerImg from "@/assets/Images/TutorCoverProfile.png";
import blankProfile from '@/assets/blank-profile.png';

// Tab Components Imports
import ProfileInfo from "@/components/tutorProfile/ProfileInfo.vue";
import ClassSchedules from "@/components/tutorProfile/classSchedules.vue";
import TeachingNotices from "@/components/tutorProfile/teachingNotices.vue";
// import Programmes from "@/components/tutorProfile/ProfileInfo.vue"; // Note: Programmes is using ProfileInfo @Githma - UI has not been designed yet
import { generateSignedUrl } from "@/api/storage";
import { ConfirmDialog } from "primevue";


const props = defineProps({
  innerMap: {
    type: Boolean,
    default: false,
  },
  tutorId: {
    type: String,
    default: null
  },
});

const route = useRoute();
const authStore = useAuthStore();

// --- STATE & COMPUTED ---

const profile = ref(null);
const feedbacks = ref(null);
const reviewsPagination = ref({ totalReviews: 0, page: 1, totalPages: 1, limit: 5 });
const currentTutorId = ref(null);
const publishedNotices = ref([]);
const bannerImgRef = ref(bannerImg);
const confirm = useConfirm();
const role = computed(() => authStore.user?.role);
const enrollmentStatus = ref(null);

const canEnroll = computed(() => role.value === 'student' && enrollmentStatus.value === false);

const classTypeInfo = computed(() => {
  if (!profile.value) return '';
  const levels = profile.value.teachingLevels ?? [];
  const individual = levels.some(l => l.individualClass?.available);
  const group = levels.some(l => l.groupClass?.available);

  if (individual && group) {
    return 'Individual & Group Classes';
  } else if (individual) {
    return 'Individual Classes';
  } else if (group) {
    return 'Group Classes';
  }
  return '';
});

const formattedTutorType = computed(() => {
  if (!profile.value?.tutorType) return '';
  const foundOccupation = occupation.find(
    (item) => item.key === profile.value.tutorType
  );
  return foundOccupation ? foundOccupation.value : capitalize(profile.value.tutorType);
});

const bannerStyleObject = computed(() => ({
  backgroundImage: `url(${bannerImgRef.value})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  width: "100%",
}));

const tabs = ref([
  { title: 'Profile info', value: '0', component: shallowRef(ProfileInfo) },
  { title: 'Class Schedules', value: '1', component: shallowRef(ClassSchedules) },
  { title: 'Teaching Notices', value: '2', component: shallowRef(TeachingNotices) },
]);

// Formatting for cleaner 12-hour time (e.g., "5:00 PM")
const timeFormat = { hour: 'numeric', minute: '2-digit', hour12: true };

const calendarOptions = ref({
  plugins: [dayGridPlugin, timeGridPlugin],
  initialView: window.innerWidth < 1024 ? 'timeGridDay' : 'timeGridWeek',
  headerToolbar: {
    left: 'title',
    center: '',
    right: 'prev,today,next',
  },
  titleFormat: {
    year: 'numeric',
    month: 'long'
  },
  height: 'auto',
  contentHeight: 'auto',
  aspectRatio: 1.5,
  handleWindowResize: true,
  windowResizeDelay: 100,
  windowResize: (arg) => {
    const calendar = arg.view.calendar;
    if (window.innerWidth < 1024 && arg.view.type !== 'timeGridDay') {
      calendar.changeView('timeGridDay');
    } else if (window.innerWidth >= 1024 && arg.view.type !== 'timeGridWeek') {
      calendar.changeView('timeGridWeek');
    }
  },
  expandRows: true,
  nowIndicator: true,
  allDaySlot: false,
  dayHeaderFormat: { day: 'numeric', weekday: 'short' },
  events: [], // Initialize events as an empty array
  eventContent: function (arg) {
    const subject = arg.event.title;
    const classType = arg.event.extendedProps.classType;
    const location = arg.event.extendedProps.location;
    const withWho = arg.event.extendedProps.with;

    // Use formatted time
    const time = arg.event.start.toLocaleTimeString('en-US', timeFormat) + ' - ' + arg.event.end.toLocaleTimeString('en-US', timeFormat);

    let customHtml = document.createElement('div');
    customHtml.innerHTML = `
      <div class="fc-event-main-frame" style="text-align: center; padding-top: 20px">
        <div class="fc-event-title" style="font-weight:900">${subject}</div>
        <div class="event-description-snippet">${classType}</div>
        <div class="event-description-snippet">${location}</div>
        <div class="event-description-snippet">${time}</div>
        <div class="event-description-snippet" style="text-align: center"><b>${withWho}</b></div>
      </div>    
    `;

    return { domNodes: [customHtml] }
  }
});

const profileImage = ref(blankProfile);

watchEffect(async () => {
    if (profile.value?.professionalPhotoURL) {
        profileImage.value = await generateSignedUrl(profile.value.professionalPhotoURL);
    } else {
        profileImage.value = blankProfile;
    }
});

// --- METHODS ---

const fetchProfile = async (id) => {
  try {
    const res = await api.publicProfile(id);
    profile.value = res.data.data;
    await mergeReviewStats(id);
  } catch (error) {
    console.error("Failed to fetch tutor profile:", error);
  }
}

const mergeReviewStats = async (id) => {
  try {
    const res = await api.fetchPublicProfiles();
    const tutors = res.data?.data?.tutors ?? [];
    const match = tutors.find(t => t._id === id);
    if (match && profile.value) {
      profile.value.averageRating = match.averageRating;
      profile.value.reviewCount = match.reviewCount;
    }
  } catch (error) {
    console.error("Failed to fetch tutor review stats:", error);
  }
}
const fetchTutorReviews = async (id, page = 1) => {
  try {
    currentTutorId.value = id;
    const res = await api.getStudentReviews(id, page, reviewsPagination.value.limit);
    feedbacks.value = res.data.data.reviewsList ?? [];
    if (res.data.data.pagination) {
      reviewsPagination.value = res.data.data.pagination;
    }
  } catch (err) {
    console.error("Unexpected error:", err);
  }
};

const onReviewPageChange = (page) => {
  if (currentTutorId.value) {
    fetchTutorReviews(currentTutorId.value, page);
  }
};

const processScheduleData = (rawData) => {
  const COLORS = {
    PASSED: { backgroundColor: '#EF9A9A', borderColor: '#E57373', textColor: '#C62828' },
    NEXT: { backgroundColor: '#A5D6A7', borderColor: '#81C784', textColor: '#388E3C' },
    FUTURE: { backgroundColor: '#90CAF9', borderColor: '#64B5F6', textColor: '#1565C0' }
  };
  const now = new Date();

  const sortedClasses = rawData
    .map(item => ({
      ...item,
      startDate: new Date(item.start)
    }))
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

  const futureClasses = sortedClasses.filter(item => item.startDate.getTime() > now.getTime());
  const nextClassStart = futureClasses.length > 0 ? futureClasses[0].startDate.getTime() : null;

  const processedEvents = sortedClasses.map(classItem => {
    const classStartTimestamp = classItem.startDate.getTime();
    let colorProfile = COLORS.FUTURE;

    if (classStartTimestamp < now.getTime()) {
      colorProfile = COLORS.PASSED;
    } else if (classStartTimestamp === nextClassStart) {
      colorProfile = COLORS.NEXT;
    }

    const subjectTitle = classItem.subject ? classItem.subject.charAt(0).toUpperCase() + classItem.subject.slice(1) : 'Unknown';

    return {
      title: `${subjectTitle}`,
      start: classItem.start.replace('Z', ''),
      end: classItem.end.replace('Z', ''),
      extendedProps: {
        classType: classItem.classType,
        location: classItem.classLocation ? classItem.classLocation : '',
        with: classItem.student ? classItem.student : '',
      },
      ...colorProfile
    };
  });

  return processedEvents;
};

const processAvailableSlots = (rawData) => {
  // Color profile for available slots (light orange)
  const AVAILABLE_COLOR = {
    backgroundColor: '#FFECB3', // Lightest orange/yellow
    borderColor: '#FFB300', // Brighter orange border
    textColor: '#E65100' // Darker text color for readability
  };

  const now = new Date();

  // 1. Filter out slots that have already ended
  const futureAvailableSlots = rawData.filter(item => {
    // Assuming 'end' field indicates the end time of the slot
    const slotEndTime = new Date(item.end);
    return slotEndTime.getTime() > now.getTime();
  });

  // 2. Process the remaining future slots
  const processedEvents = futureAvailableSlots.map(slotItem => {
    // Standardize title and strip 'Z' from start/end times for compatibility (if needed by the calendar library)
    const slotTitle = 'Available Slot';
    return {
      title: `${slotTitle}`,
      // Replace 'Z' if your calendar component requires local time format
      start: slotItem.start.replace('Z', ''),
      end: slotItem.end.replace('Z', ''),
      extendedProps: {
        classType: slotItem.note ? slotItem.note : '',
        location: '',
        with: '',
      },
      ...AVAILABLE_COLOR
    };
  });
  return processedEvents;
};

const fetchPublicSchedules = async (tutorId) => {
  try {
    const res = await api.publicClassSchedule(tutorId);
    const raw = res.data?.data;
    const events = processScheduleData(raw);
    calendarOptions.value.events = events; // This is correct. The events are set.
  } catch (err) {
    console.error('Failed to fetch public class schedule:', err);
  }
};


const requestClass = () => {
  const id = route.params.id;
  if (role.value === 'student') {
    router.push(`/student_dashboard/request-a-class/${id}`);
  } else {
    confirm.require({
      message: `Please sign in with your student account to request a class.`,
      header: `You must be an ileaen'd student!`,
      icon: 'pi pi-exclamation-triangle',
      rejectProps: {
        label: 'Create my student account',
        severity: 'secondary',
        outlined: true
      },
      acceptProps: {
        label: 'Sign in to your student account'
      },
      accept: () => {
        router.push('/login');
        localStorage.setItem('redirectAfterLogin', `/student_dashboard/request-a-class/${id}`);
      },
      reject: () => {
        router.push('/studentOnBoarding');
        localStorage.setItem('redirectAfterLogin', `/student_dashboard/request-a-class/${id}`);
      }
    });
  }
}

const fetchPublishedNotices = async (tutorId) => {
  try {
    const res = await api.listPublishedNotes(tutorId);
    publishedNotices.value = res.data?.data ?? [];
  } catch (err) {
    console.error('Failed to fetch published notices:', err);
    publishedNotices.value = [];
  }
};

const fetchEnrollmentStatus = async (tutorId) => {
  const studentId = authStore.user?.userId || authStore.user?.id || authStore.user?._id;
  if (role.value !== 'student' || !studentId) return;

  try {
    const res = await studentApi.publicEnrolledTutors(studentId);
    const enrolledTutors = res.data?.data ?? [];
    enrollmentStatus.value = enrolledTutors.some(
      (tutor) => String(tutor.tutorId || tutor._id) === String(tutorId)
    );
  } catch (err) {
    console.error('Failed to check tutor enrollment:', err);
  }
};

const getAvailability = async (tutorId) => {
  try {
    const res = await api.getAvailability(tutorId);
    const slots = processAvailableSlots(res.data?.data);
    calendarOptions.value.events = slots;
  } catch (err) {
    console.error("Failed to load availability", err);
  }
}

// --- LIFECYCLE HOOKS ---

onMounted(() => {
  const id = !props.innerMap ? route.params.id : props.tutorId;
  if (id) {
    fetchProfile(id);
    fetchTutorReviews(id);
    fetchPublicSchedules(id);
    fetchPublishedNotices(id);
    fetchEnrollmentStatus(id);
    getAvailability(id);
  }
});
</script>

<style scoped>
.banner-responsive {
  height: 500px;
  background-color: transparent;
}

.overlap-section {
  position: relative;
  margin-top: -4rem;
  background: transparent;
  padding: 0 1rem;
  margin-left: auto;
  margin-right: auto;
}

.profile-image {
  width: 160px;
  height: 160px;
  border-radius: 50%;
  border: 4px solid white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  object-fit: cover;
  margin-top: -10px;
}

.profile-info {
  display: flex;
  flex-direction: column;
  margin-top: 40px;
}

.teal-button {
  margin-top: 40px;
}

.p-tab {
  --p-tabs-tab-active-color: var(--teal-500);
  --p-tabs-tab-hover-color: var(--teal-500);
  --p-tabs-tab-font-weight: 600;
}


@media (max-width: 767px) {
  .overlap-section {
    margin-top: -3rem;
    padding: 0 0.5rem;
  }

  .profile-image {
    width: 120px;
    height: 120px;
    margin-top: -5px;
  }

  .profile-info {
    margin-top: 15px;
  }

  .teal-button {
    margin-top: 15px;
  }

  .banner-responsive {
    height: 350px;
    object-fit: contain !important;
  }
}

/* Tablet Styles */
@media (min-width: 768px) and (max-width: 1023px) {
  .overlap-section {
    margin-top: -3.5rem;
  }

  .profile-image {
    width: 140px;
    height: 140px;
  }
}
</style>